import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { parseHomeGender } from '../utils/homeGender';

const SITE_NAME = 'BarberShop';
const DEFAULT_DESCRIPTION =
  'Book haircuts, beard grooming, hair color, spa, and beauty services at trusted salons near you.';

const PAGE_META = {
  '/about': {
    title: `About us | ${SITE_NAME}`,
    description:
      "How BarberShop works: find a salon, pick a service, and book a slot — for men's barbering and women's hair and beauty.",
  },
  '/contact': {
    title: `Contact us | ${SITE_NAME}`,
    description:
      'Questions, booking help, or partnerships — send a message and we will get back to you.',
  },
  '/services': {
    title: `Services | ${SITE_NAME}`,
    description: 'Browse haircuts, color, spa, grooming, and beauty treatments.',
  },
  '/salons': {
    title: `Find salons | ${SITE_NAME}`,
    description: 'Discover salons and barbershops near you and book a slot.',
  },
  '/privacy': {
    title: `Privacy policy | ${SITE_NAME}`,
    description: 'How BarberShop collects, uses, and protects your information.',
  },
  '/terms': {
    title: `Terms of service | ${SITE_NAME}`,
    description: 'Terms that govern use of the BarberShop website and booking services.',
  },
  '/refund': {
    title: `Refund policy | ${SITE_NAME}`,
    description: 'Cancellation and refund rules for BarberShop bookings.',
  },
  '/sitemap': {
    title: `Sitemap | ${SITE_NAME}`,
    description: 'A list of BarberShop pages for visitors and search engines.',
  },
};

function siteOrigin() {
  const fromEnv = (process.env.REACT_APP_SITE_URL || '').replace(/\/$/, '');
  if (fromEnv) return fromEnv;
  if (typeof window !== 'undefined') return window.location.origin;
  return '';
}

function upsertMeta(attr, key, content) {
  if (!content) return;
  let node = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!node) {
    node = document.createElement('meta');
    node.setAttribute(attr, key);
    document.head.appendChild(node);
  }
  node.setAttribute('content', content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let node = document.head.querySelector(`link[rel="${rel}"]`);
  if (!node) {
    node = document.createElement('link');
    node.setAttribute('rel', rel);
    document.head.appendChild(node);
  }
  node.setAttribute('href', href);
}

function isPrivatePath(pathname) {
  return ['/admin', '/vendor', '/profile', '/cart', '/bookings'].some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

export default function Seo() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    const origin = siteOrigin();
    const gender = parseHomeGender(search);
    let title = `${SITE_NAME} — book salons & barbers`;
    let description = DEFAULT_DESCRIPTION;
    let canonical = origin ? `${origin}${pathname}` : pathname;

    if (pathname === '/') {
      if (gender === 'female') {
        title = `Women's hair & beauty | ${SITE_NAME}`;
        description = "Book women's haircuts, color, spa, and beauty treatments at trusted salons.";
        canonical = origin ? `${origin}/?gender=female` : '/?gender=female';
      } else {
        title = `Men's barber services | ${SITE_NAME}`;
        description = "Book men's haircuts, beard grooming, and shaves at trusted barbershops.";
        canonical = origin ? `${origin}/?gender=male` : '/?gender=male';
      }
    } else if (PAGE_META[pathname]) {
      title = PAGE_META[pathname].title;
      description = PAGE_META[pathname].description;
    } else if (pathname.startsWith('/salon/')) {
      title = `Salon | ${SITE_NAME}`;
      description = 'View salon details, services, and book an appointment.';
    } else if (pathname.startsWith('/services/')) {
      title = `Service | ${SITE_NAME}`;
      description = 'Service details, pricing, and booking on BarberShop.';
    } else if (isPrivatePath(pathname)) {
      title = `Account | ${SITE_NAME}`;
      description = DEFAULT_DESCRIPTION;
    }

    document.title = title;
    upsertMeta('name', 'description', description);
    upsertMeta('name', 'robots', isPrivatePath(pathname) ? 'noindex, nofollow' : 'index, follow');
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertLink('canonical', canonical);
  }, [pathname, search]);

  return null;
}
